import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import React, {useEffect, useState} from 'react';
import {Box, Chip, Divider, Drawer, IconButton, InputLabel, Stack, Typography} from '@mui/material';
import {Close} from '@icons/index';
import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';
import {
  ClientsService,
  clientsServicesService,
  CreateClientServiceDto,
  ServiceValidationStatusEnum
} from '@store/clients/services';
import dayjs from 'dayjs';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import {Sizes} from '@constants/sizes.constants';
import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import SelectComponent, {SelectItem} from '@components/inputs/select/Select.component';
import {FilterBaseEntityEnum, filtersService} from '@store/filters';
import {TableFilterType} from '@store/common/tableFilter.model';
import {map} from 'rxjs';
import {ClientsContract, clientsContractsService} from '@store/clients/contracts';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContractServicesModal
  from '@screens/admin/clients/details/contractedServices/manageService/steps/ContractServices.modal';
import {LoadingButton} from '@mui/lab';
import ExistingContractModal from '@screens/admin/clients/contracts/ExistingContract.modal';
import ExistingServicesModal from '@screens/admin/clients/services/ExistingServices.modal';
import {clientDetailsService} from '@store/clients/details';
import AsyncSelectComponent from '@components/inputs/asyncSelect/AsyncSelect.component';
import {clientsUnitsService} from '@store/clients/units';
import {indexationService} from '@store/settings/indexation';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {Buffer} from 'buffer';
import {UnitOfMeasure} from '@store/settings/serviceKinds';
import {ClientHead} from '@store/clients/clients.model';
import CloseIcon from '@mui/icons-material/Close';

export interface CreateServiceDrawerProps {
  handleClose?: () => void;
  fromContract?: ClientsContract;
  fromClient?: ClientHead;
}

interface CreateService {
  contractId: number;
  contractName: string;
  clientOrganisationUnitId: number;
  clientOrganisationUnitName: string;
  datahubSpec: {
    idDatahub: string;
  };
  serviceKindId: number;
  serviceKindName: string;
  clientWasteName: string;
  supplierOrganisationUnitId: number;
  supplierOrganisationUnitName: string;
  availableFrom: string;
  buyPrice: number;
  wasteId: number;
  wasteName: string;
  containerId: number;
  containerName: string;
}

interface CreateContract {
  invoiceAddress: {
    invoiceAddressId: number;
    name: string;
    addressCode: string;
  };
  datahubSpec: {
    idDatahub: string;
    datahubOrigin: string;
  };
  startDate: string;
  contractReference: string;
  contractName: string;
  clientOrganisationUnitId: number;
  clientOrganisationUnitName: string;
  clientOrganisationId: number;
  containerId: number;
  containerName: string;
  wasteId: number;
  wasteName: string;
  services: {
    datahubSpec: {
      idDatahub: string;
    };
    serviceKindId: number;
    serviceKindName: string;
    clientWasteName: string;
    supplierOrganisationUnitId: number;
    supplierOrganisationUnitName: string;
    availableFrom: string;
    buyPrice: number;
  }[];
}

const CreateServiceDrawer = (props: CreateServiceDrawerProps) => {
  const {handleClose, fromContract, fromClient} = props;

  const [searchParams, setSearchParams] = useSearchParams();
  const [client, setClient] = useState<SelectItem | undefined>(undefined);

  function getLastPrice(service: any) {
    const pricePeriods = service.pricePeriods;
    const lastPrice = pricePeriods.find((pricePeriod) => pricePeriod.toDate === null);

    return lastPrice?.sellPrices[0].unitPriceInCents / 100;
  }

  function getLastCost(service: any) {
    const pricePeriods = service.pricePeriods;
    const lastPrice = pricePeriods.find((pricePeriod) => pricePeriod.toDate === null);

    return lastPrice?.sellPrices[0].unitCostInCents / 100;
  }

  useEffect(() => {
    if (fromClient) {
      setClient({value: fromClient.id, label: fromClient.name || ''});
    }

    if (fromContract) {
      setCreateService({
        contract: {value: fromContract.id, label: fromContract.contractName},
        clientUnit: {
          value: fromContract.clientOrganisationUnitId,
          label: fromContract.clientOrganisationUnitName,
          data: {organisationId: fromContract.clientOrganisationId}
        },
        waste: {value: fromContract.services?.[0]?.waste?.id, label: fromContract.services?.[0]?.wasteName},
        container: {value: fromContract.services?.[0]?.container?.id, label: fromContract.services?.[0]?.containerName},
        services: [],
      });
    }

    const type = searchParams.get('type');
    const b64object = searchParams.get('object');
    if (type === 'CONTRAT' && b64object) {
      const paramStr = Buffer.from(b64object, 'base64').toString();
      const paramContract: CreateContract = JSON.parse(paramStr);
      setCreateService({
        clientInvoiceAddress: paramContract.invoiceAddress ? {
          value: paramContract.invoiceAddress.invoiceAddressId,
          label: paramContract.invoiceAddress.addressCode + ' - ' + paramContract.invoiceAddress.name,
        } : undefined,
        contractName: paramContract.contractName,
        datahubSpec: {
          idDatahub: paramContract.datahubSpec.idDatahub,
          additionalInformation: paramContract.contractReference,
        },
        clientUnit: {
          value: paramContract.clientOrganisationUnitId,
          label: paramContract.clientOrganisationUnitName,
          data: {organisationId: paramContract.clientOrganisationId}
        },
        waste: paramContract.wasteId ? {value: paramContract.wasteId, label: paramContract.wasteName} : undefined,
        container: paramContract.containerId ? {
          value: paramContract.containerId,
          label: paramContract.containerName
        } : undefined,
        services: paramContract.services.map((s) => ({
          kind: {value: s.serviceKindId, label: s.serviceKindName},
          supplierUnit: {value: s.supplierOrganisationUnitId, label: s.supplierOrganisationUnitName},
          unitCostInCents: s.buyPrice,
          validFromDate: s.availableFrom ? new Date(s.availableFrom) : dayjs().startOf('year').toDate(),
          canBeRequested: false,
          clientInvoicingRequired: true,
          supplierInvoicingRequired: true,
          interco: false,
          intersite: false,
          unitPriceInCents: 0,
          pctBuyDiscountRule: 0,
          pctSellDiscountRule: 0,
          clientWasteName: s.clientWasteName,
          datahubSpec: {
            idDatahub: s.datahubSpec.idDatahub,
            additionalInformation: ''
          },
          validationStatus: ServiceValidationStatusEnum.WAITING_FOR_SP,
        }))
      });
    } else if (type === 'SERVICE' && b64object) {
      const paramStr = Buffer.from(b64object, 'base64').toString();
      const paramService: CreateService = JSON.parse(paramStr);

      setCreateService({
        contract: {value: paramService.contractId, label: paramService.contractName},
        clientUnit: {value: paramService.clientOrganisationUnitId, label: paramService.clientOrganisationUnitName},
        waste: {value: paramService.wasteId, label: paramService.wasteName},
        container: {value: paramService.containerId, label: paramService.containerName},
        services: [
          {
            kind: {value: paramService.serviceKindId, label: paramService.serviceKindName},
            supplierUnit: {
              value: paramService.supplierOrganisationUnitId,
              label: paramService.supplierOrganisationUnitName
            },
            unitCostInCents: paramService.buyPrice,
            validFromDate: paramService.availableFrom ? new Date(paramService.availableFrom) : dayjs().startOf('year').toDate(),
            canBeRequested: false,
            clientInvoicingRequired: true,
            supplierInvoicingRequired: true,
            interco: false,
            intersite: false,
            unitPriceInCents: 0,
            pctBuyDiscountRule: 0,
            pctSellDiscountRule: 0,
            clientWasteName: paramService.clientWasteName,
            datahubSpec: {
              idDatahub: paramService.datahubSpec.idDatahub,
              additionalInformation: ''
            }
          }
        ]
      });
    } else if (type === 'DUPLICATE' && b64object) {
      clientsContractsService.getContract(b64object).subscribe({
        next: (contract) => {
          setCreateService({
            clientUnit: {
              value: contract.clientOrganisationUnitId,
              label: contract.clientOrganisationUnitName,
              data: {organisationId: contract.clientOrganisationId}
            },
            waste: {value: contract.services?.[0]?.waste?.id, label: contract.services?.[0]?.wasteName},
            container: {value: contract.services?.[0]?.container?.id, label: contract.services?.[0]?.containerName},
            contractName: contract.contractName,
            services: contract.services.map((s) => ({
              kind: {value: s.kind.id, label: s.kindName},
              supplierUnit: {value: s.supplierOrgUnitId, label: s.supplierOrganisationUnitName},
              unitCostInCents: getLastCost(s),
              validFromDate: dayjs().startOf('year').utc().toDate(),
              canBeRequested: false,
              clientInvoicingRequired: true,
              supplierInvoicingRequired: true,
              interco: false,
              intersite: false,
              unitPriceInCents: getLastPrice(s),
              pctBuyDiscountRule: 0,
              pctSellDiscountRule: 0,
              clientWasteName: s.clientWasteName,
            }))
          });
        },
      });
    }
  }, [searchParams, fromContract]);

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();

  const [supplier, setSupplier] = useState<SelectItem | undefined>(undefined);
  const [showContractServices, setShowContractServices] = useState<string | number | false>(false);
  const [showExistingContract, setShowExistingContract] = useState<string | number | false>(false);
  const [showExistingServices, setShowExistingServices] = useState<ClientsService[]>([]);

  const [createService, setCreateService] = useState<CreateClientServiceDto>({services: []});

  const [loading, setLoading] = useState(false);

  const removeKind = (index: number) => {
    const newServices = [...createService.services];
    newServices.splice(index, 1);
    setCreateService({
      ...createService,
      services: newServices
    });
  };

  const addKind = (kind: SelectItem) => {
    setCreateService({
      ...createService,
      services: [
        ...createService.services,
        {
          kind: kind,
          validFromDate: dayjs().startOf('year').toDate(),
          pctBuyDiscountRule: 0,
          pctSellDiscountRule: 0,
          unitCostInCents: 0,
          unitPriceInCents: 0,
          clientWasteName: '',
          interco: false,
          intersite: false,
          canBeRequested: false,
          clientInvoicingRequired: true,
          supplierInvoicingRequired: true,
          validationStatus: ServiceValidationStatusEnum.WAITING_FOR_SP,
        },
      ]
    });
  };

  const handleSave = (force = false) => {
    setLoading(true);
    clientsServicesService.createClientServiceWithSupplier({
      clientServices: createService.services.map((s) => ({
        execUnitName: s.execUnitName,
        kindId: s.kind.value,
        supplierOrganisationUnitId: s.supplierUnit?.value,
        clientWasteName: s.clientWasteName,
        pctBuyDiscountRule: s.pctBuyDiscountRule,
        pctSellDiscountRule: s.pctSellDiscountRule,
        newSellPrice: {
          unitPriceInCents: s.unitPriceInCents * 100,
          unitCostInCents: s.unitCostInCents * 100,
          fromDate: s.validFromDate,
        },
        validFromDate: s.validFromDate,
        validToDate: s.validToDate,
        canBeRequested: s.canBeRequested,
        interco: s.interco,
        intersite: s.intersite,
        clientInvoicingRequired: s.clientInvoicingRequired,
        supplierInvoicingRequired: s.supplierInvoicingRequired,
        sellIndexationId: s.sellIndexation?.value,
        buyIndexationId: s.buyIndexation?.value,
        datahubSpec: s.datahubSpec,
        validationStatus: s.validationStatus
      })),
      clientOrganisationUnitId: createService.clientUnit?.value,
      wasteId: createService.waste?.value,
      containerId: createService.container?.value,
      contractId: createService.contract?.value,
      clientInvoiceAddressId: createService.clientInvoiceAddress?.value,
      contractName: createService.contractName,
      datahubSpec: createService.datahubSpec,
      force: force,
    }).subscribe({
      next: () => {
        setLoading(false);
        enqueueSnackbar(t('clients.details.contractedServices.manage.success.create'), {variant: 'success'});
        if (handleClose) {
          handleClose();
        } else {
          navigate('..');
        }

      },
      error: (err) => {
        setLoading(false);
        if (err.text === 'CONTRACT_FOUND') {
          setShowExistingContract(err.data.data);
        } else if (err.text === 'SAME_SERVICES_FOUND') {
          setShowExistingServices(err.data.data);
        }
      },
    });
  };

  const updateClientService = (index: number, name: string, value: any) => {
    const services = [...createService.services];
    services[index] = {...services[index], [name]: value};
    setCreateService({...createService, services});
  };
  const updateService = (name: string) => (data: any) => setCreateService((state) => ({...state, [name]: data}));


  const errors = {
    kinds: getFieldError(createService.services, FieldValidationType.REQUIRED_ARRAY),
    clientUnit: getFieldError(createService.clientUnit, FieldValidationType.REQUIRED_SELECT_ITEM),
    contractName: !createService.contract && getFieldError(createService.contractName, FieldValidationType.REQUIRED_STRING),
    contractAddress: !createService.contract && getFieldError(createService.clientInvoiceAddress, FieldValidationType.REQUIRED_SELECT_ITEM),
    services: createService?.services?.map((service) => ({
      supplierUnit: getFieldError(service.supplierUnit, FieldValidationType.REQUIRED_SELECT_ITEM),
      clientWasteName: getFieldError(service.clientWasteName, FieldValidationType.REQUIRED_STRING),
      unitPriceInCents: getFieldError(service.unitPriceInCents, FieldValidationType.REQUIRED_NUMBER),
      unitCostInCents: getFieldError(service.unitCostInCents, FieldValidationType.REQUIRED_NUMBER),
      pctBuyDiscountRule: getFieldError(service.pctBuyDiscountRule, FieldValidationType.REQUIRED_NUMBER),
      pctSellDiscountRule: getFieldError(service.pctSellDiscountRule, FieldValidationType.REQUIRED_NUMBER),
      validFromDate: getFieldError(service.validFromDate, FieldValidationType.REQUIRED_DATE),
      validToDate: getFieldError(service.validToDate, FieldValidationType.DATE),
    }))
  };

  return (
    <Drawer anchor="right" open onClose={() => {
      if (handleClose != null) {
        handleClose();
      } else {
        navigate('..');
      }
    }}>
      <Stack overflow="hidden">
        <Stack direction="row" justifyContent="flex-end">
          <IconButton onClick={() => {
            if (handleClose != null) {
              handleClose();
            } else {
              console.log('test');
              navigate('..');
            }
          }}>
            <Close/>
          </IconButton>
        </Stack>
        <Stack pr="10px">
          <Stack direction="row" alignItems="center" spacing={1.5} pb="8px">
            <Typography fontSize={24} fontWeight="700">
              {t('clients.details.contractedServices.manage.title.add')}
            </Typography>
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal"/>
        </Stack>
        <Stack className="scrollable" overflow="auto" pb="24px" pr="10px">
          <Stack py="20px" spacing={2}>
            <Typography fontSize={16} fontWeight="700">
              {t('clients.details.contractedServices.manage.client')}
            </Typography>
            <Stack spacing={2}>
              <AsyncSelectWithSearchComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('clients.details.contractedServices.manage.client')}
                placeholder={t('global.selectOption')}
                handleChange={(client) => setClient(client)}
                required={false}
                value={client}
                getOptions={(search) => filtersService.getFilters(TableFilterType.CLIENT_ORGANISATION, FilterBaseEntityEnum.NONE, [], search).pipe(
                  map((options) => options.map((o) => ({label: o.name || '-', value: o.id})))
                )}/>
              <AsyncSelectWithSearchComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('clients.details.contractedServices.manage.clientUnit')}
                placeholder={t('global.selectOption')}
                handleChange={(clientUnit) => updateService('clientUnit')(clientUnit)}
                required
                value={createService.clientUnit}
                error={errors.clientUnit}
                getOptions={(search) => clientsUnitsService.searchClientsUnits(search, client ? [client.value] : []).pipe(
                  map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id, data: o})))
                )}/>
            </Stack>
          </Stack>
          {createService.clientUnit && <Stack py="20px" spacing={2}>
              <Typography fontSize={16} fontWeight="700">
                {t('clients.details.contractedServices.manage.contract')}
              </Typography>
              <AsyncSelectWithSearchComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('clients.details.contractedServices.manage.contract')}
                  placeholder={t('global.selectOption')}
                  handleChange={(contract) => {
                    updateService('contract')(contract);
                    if (!contract) {
                      updateService('waste')(undefined);
                      updateService('container')(undefined);
                    } else {
                      const service = (contract.data as ClientsContract).services[0];
                      updateService('waste')({
                        label: service.waste.name,
                        value: service.waste.id,
                      });
                      updateService('container')({
                        label: service.container.name,
                        value: service.container.id,
                      });
                    }

                  }}
                  required={false}
                  value={createService.contract}
                  getOptions={(search) => clientsContractsService.getClientUnitContract(createService.clientUnit?.value, search).pipe(
                    map((options) => options.map((o) => {
                      let name = '';
                      const waste = o.services?.[0]?.wasteName;
                      const container = o.services?.[0]?.containerName;
                      if (waste && container) {
                        name = `${waste} - ${container}`;
                      } else if (waste) {
                        name = waste;
                      } else if (container) {
                        name = container;
                      } else {
                        name = o.contractName;
                      }
                      return {label: name || '-', value: o.id, data: o};
                    }))
                  )}/>
            {createService.contract && <Stack direction="row" justifyContent="space-between" spacing={2}>
                <InputLabel sx={{width: Sizes.drawerInputLabelWidth}} required={false}>
                  {t('operations.executions.manage.showContractServices')}*
                </InputLabel>
                <IconButton
                    onClick={() =>
                      setShowContractServices(createService.contract?.value)
                    }
                >
                    <OpenInNewIcon color="primary"/>
                </IconButton>
            </Stack>}
            {!createService.contract && (
              <>
                <Stack direction="row" justifyContent="center">
                  <Divider sx={{width: '75%'}} orientation="horizontal">OU</Divider>
                </Stack>
                <TextFieldComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  required
                  label={t('clients.details.contracts.manage.name')}
                  placeholder={t('global.writeSomething')}
                  handleChange={(value) => updateService('contractName')(value)}
                  value={createService.contractName || ''}
                  error={errors.contractName}
                />
                <AsyncSelectComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('unitDetails.contacts.address.internalName')}
                  required
                  placeholder={t('global.selectOption')}
                  getOptions={() => clientDetailsService.getInvoiceAddresses(createService.clientUnit?.data.organisationId).pipe(
                    map((options) => {
                        return [{label: 'PAR DEFAUT (Adresse Organisation)', value: undefined}, ...(options.map((o) => ({
                          label: o.addressCode + ' - ' + o.internalName || '-',
                          value: o.clientInvoiceAddressId || 0,
                          data: o,
                        })))];
                      }
                    ))}
                  value={createService.clientInvoiceAddress}
                  error={errors.contractAddress}
                  handleChange={(value) => {
                    updateService('clientInvoiceAddress')(value);
                  }}/>
                <TextFieldComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('clients.details.contracts.manage.datahubId')}
                  placeholder={t('global.writeSomething')}
                  value={createService.datahubSpec?.idDatahub}/>
                <TextFieldComponent
                  labelWidth={Sizes.drawerInputLabelWidth}
                  label={t('clients.details.contracts.manage.additionalInformation')}
                  placeholder={t('global.writeSomething')}
                  value={createService.datahubSpec?.additionalInformation}/>
              </>)} </Stack>}
          <Stack py="20px" spacing={2}>
            <Typography fontSize={16} fontWeight="700">
              {t('clients.details.contractedServices.manage.operationalData')}
            </Typography>
            <Stack spacing={2}>
              <AsyncSelectWithSearchComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('clients.details.contractedServices.manage.serviceKind')}
                placeholder={t('global.selectOptions')}
                handleChange={(kind) => {
                  if (kind && kind.value !== '') {
                    addKind(kind);
                  }
                }}
                value={undefined}
                error={errors.kinds}
                getOptions={(search) => filtersService.getFilters(TableFilterType.SERVICE_KIND, FilterBaseEntityEnum.NONE, [], search).pipe(
                  map((options) => options.map((o) => ({label: o.name || '-', value: o.id})))
                )}/>
              <AsyncSelectWithSearchComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('clients.details.contractedServices.manage.waste')}
                placeholder={t('global.selectOption')}
                handleChange={(waste) => updateService('waste')(waste)}
                required={false}
                value={createService.waste}
                readOnly={createService.contract != null}
                getOptions={(search) => filtersService.getFilters(TableFilterType.WASTE, FilterBaseEntityEnum.NONE, [], search).pipe(
                  map((options) => options.map((o) => ({label: o.name || '-', value: o.id})))
                )}/>
              <AsyncSelectWithSearchComponent
                labelWidth={Sizes.drawerInputLabelWidth}
                label={t('clients.details.contractedServices.manage.container')}
                placeholder={t('global.selectOption')}
                handleChange={(container) => updateService('container')(container)}
                required={false}
                readOnly={createService.contract != null}
                value={createService.container}
                getOptions={(search) => filtersService.getFilters(TableFilterType.CONTAINER, FilterBaseEntityEnum.NONE, [], search).pipe(
                  map((options) => options.map((o) => ({label: o.name || '-', value: o.id})))
                )}/>
            </Stack>
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal"/>
          {createService.services.map((service, index) => <>
              <Stack py="20px" spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography fontSize={16} fontWeight="700">
                    {service.kind?.label?.toUpperCase()}
                  </Typography>
                  <IconButton>
                    <CloseIcon onClick={() => removeKind(index)}></CloseIcon>
                  </IconButton>
                </Stack>
                <Stack spacing={2}>
                  <AsyncSelectWithSearchComponent
                    labelWidth={Sizes.drawerInputLabelWidth}
                    label={t('clients.details.contractedServices.manage.supplier')}
                    placeholder={t('global.selectOption')}
                    handleChange={(supplier) => setSupplier(supplier)}
                    required={false}
                    value={supplier}
                    getOptions={(search) => filtersService.getFilters(TableFilterType.SUPPLIER_ORGANISATION, FilterBaseEntityEnum.NONE, [], search).pipe(
                      map((options) => options.map((o) => ({label: o.name || '-', value: o.id})))
                    )}/>
                  <AsyncSelectWithSearchComponent
                    labelWidth={Sizes.drawerInputLabelWidth}
                    label={t('clients.details.contractedServices.manage.supplierUnit')}
                    placeholder={t('global.selectOption')}
                    handleChange={(supplierUnit) => updateClientService(index, 'supplierUnit', supplierUnit)}
                    required
                    value={service.supplierUnit}
                    error={errors.services[index].supplierUnit}
                    getOptions={(search) => filtersService.getFilters(TableFilterType.SUPPLIER_ORGANISATION_UNIT, FilterBaseEntityEnum.NONE, supplier ? [{
                      type: TableFilterType.SUPPLIER_ORGANISATION,
                      values: [supplier]
                    }] : [], search).pipe(
                      map((options) => options.map((o) => ({label: o.name || '-', value: o.id})))
                    )}/>
                  <TextFieldComponent
                    labelWidth={Sizes.drawerInputLabelWidth}
                    label={t('clients.details.contractedServices.manage.name')}
                    placeholder={t('global.writeSomething')}
                    handleChange={(clientWasteName) => updateClientService(index, 'clientWasteName', clientWasteName)}
                    value={service.clientWasteName || ''}
                    error={errors.services[index].clientWasteName}
                    required/>
                  <SelectComponent
                    color="secondary"
                    labelWidth={Sizes.drawerInputLabelWidth}
                    label={t('clients.details.contractedServices.manage.execUnitName')}
                    placeholder={t('global.selectOption')}
                    handleChange={(value) => updateClientService(index, 'execUnitName', value)}
                    value={service.execUnitName}
                    items={UnitOfMeasure.selectItems()}
                  />
                  <Stack direction="row" spacing={2}>
                    <InputLabel sx={{width: Sizes.drawerInputLabelWidth}}>
                      {t('clients.details.contractedServices.manage.price')}*
                    </InputLabel>
                    <Box flex={0.5}>
                      <NumberTextFieldComponent
                        labelWidth={Sizes.drawerInputLabelWidth}
                        placeholder={t('global.writeSomething')}
                        pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
                        handleChange={(value) => updateClientService(index, 'unitCostInCents', value)}
                        value={service.unitCostInCents}
                        startAdornment={<Typography fontSize={14} fontWeight="400">A</Typography>}
                        error={errors.services[index].unitCostInCents}
                        required/>
                    </Box>
                    <Box flex={0.5}>
                      <NumberTextFieldComponent
                        placeholder={t('global.writeSomething')}
                        pattern={new RegExp(/^-?(\d*|\d+)[,.]?\d*$/)}
                        startAdornment={<Typography fontSize={14} fontWeight="400">V</Typography>}
                        handleChange={(value) => updateClientService(index, 'unitPriceInCents', value)}
                        value={service.unitPriceInCents}
                        error={errors.services[index].unitPriceInCents}
                        required/>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <InputLabel sx={{width: Sizes.drawerInputLabelWidth}}>
                      {t('clients.details.contractedServices.manage.buyDiscount')}*
                    </InputLabel>
                    <Box flex={0.5}>
                      <NumberTextFieldComponent
                        labelWidth={Sizes.drawerInputLabelWidth}
                        placeholder={t('global.writeSomething')}
                        pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
                        handleChange={(pctBuyDiscountRule) => updateClientService(index, 'pctBuyDiscountRule', pctBuyDiscountRule)}
                        value={service.pctBuyDiscountRule}
                        startAdornment={<Typography fontSize={14} fontWeight="400">A</Typography>}
                        endAdornment={<Typography fontSize={14} fontWeight="400">%</Typography>}
                        error={errors.services[index].pctBuyDiscountRule}
                        required/>
                    </Box>
                    <Box flex={0.5}>
                      <NumberTextFieldComponent
                        placeholder={t('global.writeSomething')}
                        pattern={new RegExp(/^(\d*|\d+)[,.]?\d*$/)}
                        startAdornment={<Typography fontSize={14} fontWeight="400">V</Typography>}
                        endAdornment={<Typography fontSize={14} fontWeight="400">%</Typography>}
                        handleChange={(pctSellDiscountRule) => updateClientService(index, 'pctSellDiscountRule', pctSellDiscountRule)}
                        value={service.pctSellDiscountRule}
                        error={errors.services[index].pctSellDiscountRule}
                        required/>
                    </Box>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <InputLabel sx={{width: Sizes.drawerInputLabelWidth}}>
                      {t('clients.details.contractedServices.manage.validity')}*
                    </InputLabel>
                    <Box flex={0.5}>
                      <DatePickerComponent
                        placeholder={t('global.selectDate')}
                        value={service.validFromDate}
                        inputWidth={120}
                        handleChange={(validFromDate) => {
                          updateClientService(index, 'validToDate', service.validToDate && dayjs(service.validToDate).isBefore(validFromDate) ? validFromDate : service.validToDate);
                          updateClientService(index, 'validFromDate', validFromDate);
                        }}
                        error={errors.services[index].validFromDate}/>
                    </Box>
                    <Box flex={0.5}>
                      <DatePickerComponent
                        placeholder={t('global.selectDate')}
                        value={service.validToDate}
                        inputWidth={120}
                        handleChange={(validToDate) => {
                          updateClientService(index, 'validToDate', validToDate);
                          updateClientService(index, 'validFromDate', service.validFromDate && dayjs(service.validFromDate).isAfter(validToDate) ? validToDate : service.validFromDate);
                        }}/>
                    </Box>
                  </Stack>
                  <AsyncSelectComponent
                    getOptions={() => indexationService.getIndexations().pipe(map((indexations) => indexations.map((indexation) => ({
                      value: indexation.id,
                      label: `${indexation.name} - ${indexation.currentIndexationRate?.rateInPercent}%`
                    }))))}
                    label={t('clients.details.contractedServices.manage.buyIndexation')}
                    labelWidth={Sizes.drawerInputLabelWidth}
                    handleChange={(indexation) => {
                      updateClientService(index, 'buyIndexation', indexation);
                    }}
                    placeholder={t('global.select')}
                    value={service.buyIndexation}
                  />
                  <AsyncSelectComponent
                    getOptions={() => indexationService.getIndexations().pipe(map((indexations) => indexations.map((indexation) => ({
                      value: indexation.id,
                      label: `${indexation.name} - ${indexation.currentIndexationRate?.rateInPercent}%`
                    }))))}
                    label={t('clients.details.contractedServices.manage.sellIndexation')}
                    labelWidth={Sizes.drawerInputLabelWidth}
                    handleChange={(indexation) => {
                      updateClientService(index, 'sellIndexation', indexation);
                    }}
                    placeholder={t('global.select')}
                    value={service.sellIndexation}
                  />
                  <SelectComponent
                    labelWidth={Sizes.drawerInputLabelWidth}
                    label={t('clients.details.contractedServices.manage.validationStatus')}
                    placeholder={t('global.selectOption')}
                    handleChange={(value) => updateClientService(index, 'validationStatus', value)}
                    value={service.validationStatus}
                    items={ServiceValidationStatusEnum.selectItems()}/>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                      label={t('clients.details.contractedServices.manage.canBeRequested')}
                      style={{
                        backgroundColor: service.canBeRequested ? '#6FD969' : '#F4F6F8',
                        color: service.canBeRequested ? '#192133' : '#868F9C',
                      }}
                      onClick={() => updateClientService(index, 'canBeRequested', !service.canBeRequested)}
                    />
                    <Chip
                      label={t('clients.details.contractedServices.manage.clientInvoicingRequired')}
                      style={{
                        backgroundColor: service.clientInvoicingRequired ? '#6FD969' : '#F4F6F8',
                        color: service.clientInvoicingRequired ? '#192133' : '#868F9C',
                      }}
                      onClick={() => updateClientService(index, 'clientInvoicingRequired', !service.clientInvoicingRequired)}
                    />
                    <Chip
                      label={t('clients.details.contractedServices.manage.supplierInvoicingRequired')}
                      style={{
                        backgroundColor: service.supplierInvoicingRequired ? '#6FD969' : '#F4F6F8',
                        color: service.supplierInvoicingRequired ? '#192133' : '#868F9C',
                      }}
                      onClick={() => updateClientService(index, 'supplierInvoicingRequired', !service.supplierInvoicingRequired)}
                    />
                    <Chip
                      label={t('clients.details.contractedServices.manage.intersite')}
                      style={{
                        backgroundColor: service.intersite ? '#6FD969' : '#F4F6F8',
                        color: service.intersite ? '#192133' : '#868F9C',
                      }}
                      onClick={() => updateClientService(index, 'intersite', !service.intersite)}
                    />
                  </Stack>
                  <TextFieldComponent
                    labelWidth={Sizes.drawerInputLabelWidth}
                    label={t('clients.details.contracts.manage.datahubId')}
                    placeholder={t('global.writeSomething')}
                    value={service.datahubSpec?.idDatahub}/>
                  <TextFieldComponent
                    labelWidth={Sizes.drawerInputLabelWidth}
                    label={t('clients.details.contracts.manage.additionalInformation')}
                    placeholder={t('global.writeSomething')}
                    value={service.datahubSpec?.additionalInformation}/>
                </Stack>
              </Stack>
            </>
          )}
          <Divider variant="fullWidth" orientation="horizontal"/>
        </Stack>
        <Stack alignSelf="flex-end" spacing={3} pb="25px" pt="10px" pr="10px">
          <Stack direction="row" spacing={2}>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={loading}
              disabled={loading || checkIfErrors(errors)}
              onClick={() => handleSave(false)}
            >
              {t('global.save')}
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
      {!!showContractServices && (
        <ContractServicesModal
          showAddServiceButton={false}
          contractId={showContractServices}
          handleClose={() => setShowContractServices(false)}/>
      )}
      {showExistingServices && showExistingServices.length > 0 && (
        <ExistingServicesModal services={showExistingServices} handleForce={() => {
          handleSave(true);
          setShowExistingServices([]);
        }}
                               handleClose={() => setShowExistingServices([])}/>
      )}
      {showExistingContract && (
        <ExistingContractModal contractId={showExistingContract} handleForce={() => {
          handleSave(true);
          setShowExistingContract(false);
        }} handleUseContract={(contractId, contractName) => {
          setShowExistingContract(false);
          updateService('contract')({label: contractName, value: contractId});
        }} handleClose={() => setShowExistingContract(false)}/>
      )}
    </Drawer>
  );
};

export default CreateServiceDrawer;
